/*
 * Copyright © 2024-2026 symecode.com. All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution is prohibited.
 */


import React, { useEffect, useState } from 'react';
import BackButton from '../common/BackButton';
import { useParams, useNavigate } from 'react-router-dom';
import { Component, ComponentType, ParticipantType } from '../../types';
import apiClient from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import CompanyModal from '../common/CompanyModal';
import ComponentCollectionManager from './ComponentCollectionManager';
import './PdfDetail.css';

const PdfDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [component, setComponent] = useState<Component | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCompanyModal, setShowCompanyModal] = useState(false);

  useEffect(() => {
    if (id) {
      fetchComponent(parseInt(id));
    }
  }, [id]);

  const fetchComponent = async (componentId: number) => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.getComponentById(componentId);
      if (response.success && response.component) {
        setComponent(response.component);
      } else {
        setError(response.error || 'Failed to load component details');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred while loading component details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatPrice = (price?: number) => {
    if (price === null || price === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(price);
  };

  const handleViewComponent = async () => {
    if (!component) return;

    try {
      await apiClient.viewComponentFile(component.id);
    } catch (err: any) {
      alert('Failed to view component file: ' + (err.message || 'Unknown error'));
    }
  };

  const canEditComponent = (): boolean => {
    if (!currentUser || !component) return false;

    if (currentUser.type === ParticipantType.ADMIN) {
      return true;
    }

    if (currentUser.type === ParticipantType.MANAGER &&
        currentUser.company &&
        component.company &&
        currentUser.company.id === component.company.id) {
      return true;
    }

    return false;
  };

  const handleEdit = () => {
    if (component) {
      navigate(`/components/${component.id}/edit`);
    }
  };

  if (loading) {
    return (
      <div className="pdf-detail-container">
      <BackButton to="/components" />
        <div className="loading">Loading component details...</div>
      </div>
    );
  }

  if (error || !component) {
    return (
      <div className="pdf-detail-container">
        <div className="error-message">
          <h3>Error</h3>
          <p>{error || 'Component not found'}</p>
          <button onClick={() => navigate('/components')} className="back-button">
            Back to Components
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pdf-detail-container">
      <div className="pdf-detail-header">
        <button onClick={() => navigate('/components')} className="back-button">
          ← Back to Components
        </button>
        <h1>Component Details</h1>
      </div>

      <div className="pdf-detail-card">
        <div className="detail-section">
          <h2>General Information</h2>
          <div className="detail-grid">
            <div className="detail-item">
              <span className="detail-label">ID:</span>
              <span className="detail-value">{component.id}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Name:</span>
              <span className="detail-value">{component.name}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">File Path:</span>
              <span className="detail-value detail-filepath">{component.filePath || 'N/A'}</span>
            </div>
          </div>
          <div style={{ marginTop: '1.5rem', display: 'flex', gap: '1rem' }}>
            {component.filePath && (
              <button onClick={handleViewComponent} className="back-button" style={{ backgroundColor: '#3b82f6' }}>
                View Component File
              </button>
            )}
            {canEditComponent() && (
              <button onClick={handleEdit} className="back-button" style={{ backgroundColor: '#10b981' }}>
                Edit Component
              </button>
            )}
          </div>
        </div>

        <div className="detail-section">
          <h2>Manufacturer Information</h2>
          <div className="detail-grid">
            <div className="detail-item">
              <span className="detail-label">Manufacturer Name:</span>
              <span className="detail-value">{component.manufacturerName}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Manufacturer Code:</span>
              <span className="detail-value">{component.manufacturerCode}</span>
            </div>
          </div>
        </div>

        <div className="detail-section">
          <h2>Dates and Pricing</h2>
          <div className="detail-grid">
            <div className="detail-item">
              <span className="detail-label">Upload Date:</span>
              <span className="detail-value">{formatDate(component.uploadDate)}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Expiration Date:</span>
              <span className="detail-value">{formatDate(component.expirationDate)}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Price:</span>
              <span className="detail-value detail-price">{formatPrice(component.price)}</span>
            </div>
          </div>
        </div>

        <div className="detail-section">
          <h2>Company</h2>
          <div className="detail-grid">
            <div className="detail-item">
              <span className="detail-label">Associated Company:</span>
              {component.company ? (
                <button
                  className="company-link-button"
                  onClick={() => setShowCompanyModal(true)}
                >
                  {component.company.name}
                </button>
              ) : (
                <span className="detail-value">N/A</span>
              )}
            </div>
          </div>
        </div>

        {component.frontPageTemplate && (
          <div className="detail-section">
            <h2>Front Page Template</h2>
            <div className="front-page-template-preview">
              <div
                className="template-content"
                dangerouslySetInnerHTML={{ __html: component.frontPageTemplate }}
              />
            </div>
          </div>
        )}

        {component.type === ComponentType.COLLECTION && (
          <ComponentCollectionManager collectionId={component.id} />
        )}
      </div>

      {showCompanyModal && component.company && (
        <CompanyModal
          company={component.company}
          onClose={() => setShowCompanyModal(false)}
        />
      )}
    </div>
  );
};

export default PdfDetail;
